/**
 * ganttConflictTagger.ts
 * Phase 4, Item 4 — Pure function to tag GanttPoints with conflict type.
 *
 * Given a GanttChartData (from buildGanttChartData) and a ConflictSummary
 * (from fetchScheduleConflicts), this module sets `custom.conflictType` on
 * every machineBooking point that appears in the conflict sets.
 *
 * Hard conflict: a booking whose key appears in ANY HardConflict cluster.
 * Soft conflict: a booking whose key appears in SoftConflict.bookingKey.
 * If a booking is in both hard and soft, hard takes precedence.
 *
 * The function returns a NEW GanttChartData (immutable — does not mutate
 * the input) with updated points. All non-machineBooking points pass through
 * unchanged.
 *
 * Exports:
 *   tagConflicts(chartData, conflicts) → GanttChartData
 *
 * Pure function — no DOM, no React, no side effects. Fully unit-testable.
 */

import type { GanttChartData, GanttPoint } from './masterGanttRowBuilder.js';
import type { ConflictSummary } from '../../types/schedule.js';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

/** Conflict type stored on GanttPoint.custom.conflictType */
export type ConflictType = 'hard' | 'soft' | null;

// ---------------------------------------------------------------------------
// Internal helpers
// ---------------------------------------------------------------------------

/**
 * Build a Set of all booking keys that appear in hard conflict clusters.
 * A booking is in a hard conflict if it appears in the `bookings` array of
 * ANY HardConflict entry.
 */
function buildHardSet(conflicts: ConflictSummary): Set<string> {
  const set = new Set<string>();
  for (const hc of conflicts.hard) {
    for (const bk of hc.bookings) {
      set.add(bk.key);
    }
  }
  return set;
}

/**
 * Build a Set of all booking keys that appear in soft conflicts.
 */
function buildSoftSet(conflicts: ConflictSummary): Set<string> {
  const set = new Set<string>();
  for (const sc of conflicts.soft) {
    set.add(sc.bookingKey);
  }
  return set;
}

/**
 * Resolve the conflict type for a given booking key.
 * Hard takes precedence over soft.
 */
function resolveConflictType(
  bookingKey: string,
  hardSet: Set<string>,
  softSet: Set<string>,
): ConflictType {
  if (hardSet.has(bookingKey)) return 'hard';
  if (softSet.has(bookingKey)) return 'soft';
  return null;
}

// ---------------------------------------------------------------------------
// Tag a single GanttPoint
// ---------------------------------------------------------------------------

/**
 * Return a new GanttPoint with `custom.conflictType` set if applicable.
 * Only machineBooking points are inspected; all others pass through unchanged.
 */
function tagPoint(
  point: GanttPoint,
  hardSet: Set<string>,
  softSet: Set<string>,
): GanttPoint {
  if (point.taskType !== 'machineBooking') {
    return point;
  }

  const bookingKey = point.custom?.bookingKey as string | undefined;
  if (!bookingKey) {
    // No booking key on custom — leave untouched (should not happen in practice).
    return point;
  }

  // BUG-2026-0193: a folded "Preparation" bar stands for several bookings
  // (custom.bookingKeys) — a conflict on ANY member flags the bar; hard wins.
  const keys = (point.custom?.bookingKeys as string[] | undefined) ?? [bookingKey];
  let conflictType: ConflictType = null;
  for (const key of keys) {
    const t = resolveConflictType(key, hardSet, softSet);
    if (t === 'hard') { conflictType = 'hard'; break; }
    if (t === 'soft') conflictType = 'soft';
  }

  return {
    ...point,
    custom: {
      ...point.custom,
      conflictType,
    },
  };
}

// ---------------------------------------------------------------------------
// Main export
// ---------------------------------------------------------------------------

/**
 * Tag every machineBooking GanttPoint in `chartData` with
 * `custom.conflictType: 'hard' | 'soft' | null` based on `conflicts`.
 *
 * @param chartData  Output from buildGanttChartData
 * @param conflicts  Output from fetchScheduleConflicts
 * @returns A new GanttChartData (does not mutate inputs)
 */
export function tagConflicts(
  chartData: GanttChartData,
  conflicts: ConflictSummary,
): GanttChartData {
  const hardSet = buildHardSet(conflicts);
  const softSet = buildSoftSet(conflicts);

  return {
    series: chartData.series.map(series => ({
      ...series,
      data: series.data.map(point => tagPoint(point, hardSet, softSet)),
    })),
  };
}
